import React from "react";
// import IdeasContext from "../../IdeasContext"; // Removed - using props instead
import { formatBigMonetaryValueIdeas } from "../../ideas-table-helpers/Helpers";
import { Flex, Text, Tooltip, Skeleton, Box, Stack } from "@mantine/core";
import { capitalizeFirstLetters, updateAccessorByState } from "../../../../utils";

// Type definitions for component props
interface DynamicCellProps {
  data: Record<string, any>;
  accessor: string;
  sortState?: string; // Added as prop
  renderParams?: {
    prefix?: string;
    default?: string;
    multiplyByComponentWeight?: boolean;
    backgroundColor?: string;
  };
}

// Main component
export const DynamicCell: React.FC<DynamicCellProps> = ({
  data,
  accessor,
  sortState = "total", // Default value
  renderParams = {},
}) => {

  // Remove context usage
  // const { sortState } = useContext(IdeasContext);
  const multiplyByComponentWeight = renderParams?.multiplyByComponentWeight || false;

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 DynamicCell debug:', {
      accessor,
      sortState,
      multiplyByComponentWeight,
      data: data._id || 'no-id'
    });
  }

  // Helper function to safely access nested values
  const getNestedValue = (obj: Record<string, any>, path: string): any => {
    return path
      .split(".")
      .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
  };

  // Update accessors based on the current sort state
  const totalAccessor = updateAccessorByState(accessor, "total");
  const assignedAccessor = updateAccessorByState(accessor, "assigned");
  const suggestedAccessor = updateAccessorByState(accessor, "suggested");

  // Retrieve values from the data object
  const totalValue = getNestedValue(data, totalAccessor);
  const assignedValue = getNestedValue(data, assignedAccessor);
  const suggestedValue = getNestedValue(data, suggestedAccessor);
  
  // console.log({ data, componentObj: data.componentObj });

  // Retrieve component weight, defaulting to 1 if not present
  const componentWeight = data.componentObj?.weight || 1;

  // Calculate multiplied values if required
  const multipliedTotalValue = multiplyByComponentWeight ? totalValue * componentWeight : totalValue;
  const multipliedAssignedValue = multiplyByComponentWeight ? assignedValue * componentWeight : assignedValue;
  const multipliedSuggestedValue = multiplyByComponentWeight ? suggestedValue * componentWeight : suggestedValue;

  /* console.log(renderParams?.backgroundColor) */

  /* const backgroundColor = renderParams?.backgroundColor */

  // Determine what to display based on sortState
  let displayContent;

  switch (sortState) {
  case "total":
    displayContent = (
      <Tooltip label="Total (Assigned)" withArrow>
        <Flex /* align="left" */ /* ml={13} */>
          {/* Total Value with Tooltip */}
          <Text weight={600} color="#5C5CEB">
            {formatBigMonetaryValueIdeas(multipliedTotalValue || 0, renderParams.prefix)}
          </Text>

          {/* Assigned Value in Parentheses with Tooltip */}
          <Text /* pl={5} */ weight={400} color="#5C5CEB">
              ({formatBigMonetaryValueIdeas(multipliedAssignedValue || 0, renderParams.prefix)})
          </Text>
        </Flex>
      </Tooltip>
    );
    break;

  case "assigned":
    displayContent = (
      <Tooltip label={capitalizeFirstLetters("assigned")} withArrow>
        <Text weight={600} color="#5C5CEB" /* ml={63} */>
          {formatBigMonetaryValueIdeas(multipliedAssignedValue || 0, renderParams.prefix)}
        </Text>
      </Tooltip>
    );
    break;

  case "suggested":
    displayContent = (
      <Tooltip label={capitalizeFirstLetters("suggested")} withArrow>
        <Text weight={600} color="#5C5CEB99"/*  ml={63} */>
          {formatBigMonetaryValueIdeas(multipliedSuggestedValue || 0, renderParams.prefix)}
        </Text>
      </Tooltip>
    );
    break;

  default:
    displayContent = <Text /* ml={63} */>{renderParams.default || "-"}</Text>;
  }

  return  <Flex /* sx={{backgroundColor: backgroundColor ? backgroundColor : '#fff'}} *//* justify="left" */ /* ml={23} */>{displayContent}</Flex>;
};

// Skeleton component for DynamicCell
export const DynamicCellSkeleton: React.FC<{ col: any }> = ({ col }) => {
  const columnWidth = col.width;
  const minWidth = col.minWidth;
  const maxWidth = col.maxWidth;
  
  // Dynamic cells often show multiple data elements
  const primaryWidth = columnWidth ? Math.min(parseInt(columnWidth) * 0.8, 120) : "80%";
  const secondaryWidth = columnWidth ? Math.min(parseInt(columnWidth) * 0.6, 80) : "60%";
  
  return (
    <Box 
      sx={{ 
        width: columnWidth || "100%", 
        minWidth, 
        maxWidth 
      }}
    >
      <Stack spacing={2}>
        <Skeleton height={16} width={primaryWidth} radius="sm" />
        <Skeleton height={14} width={secondaryWidth} radius="sm" />
      </Stack>
    </Box>
  );
};